import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../root/root-types';
import {
  ArithmeticOperatorInputOptions,
  BooleanInputItem,
  BooleanInputItemOptions,
  Epoch,
  EpochScreenType,
  FilterInputs,
  ImageSearchKey,
  FiltersData,
  InputItem,
  ListInputItem,
  ListInputItemOptions,
  MobileSift,
  MobileSiftScreen,
  Operator,
  RecentSearchData,
  RecentSearchTypes,
  Sift,
  SiftScreenEnum,
  SortDirection,
  SortField,
  StringInputItem,
  StringInputItemOptions,
  TreeInput,
  TreeInputItemOptions,
  MobileSiftApplied,
  MobileSearch,
  ListInputItemOptionsWithSearch,
  CalendarInputItem,
  AppliedFiltersData,
} from './model';
import type {
  CustomerFilterAttribute,
  CustomAttributeFilterItem,
} from '../customers/model';
import _, { isEmpty, isUndefined } from 'lodash';
import moment from 'moment';
import { ThreadsFitlers } from '../thread';
import {
  applySelectedFilters,
  clearSelectedFilters,
  updateFiltersData,
} from './helpers';

export const FILTER_FEATURE_KEY = 'filtersData';

export interface FiltersState {
  filters: FilterInputs;
  epoch: Epoch;
  sift: Sift;
  appliedFilters: FilterInputs;
  threadsFilters: ThreadsFitlers;
  error: string | null;
  searchKey?: string;
  imageSearchKey?: ImageSearchKey;
  searchKeyList?: string[];
  searchActive?: boolean;
  searchActiveScreen?: string;
  pagination: number;
  recentSearches: RecentSearchData;
  mobileFilters: MobileSift;
  mobileSearch: MobileSearch;
  appliedMobileFilters: MobileSiftApplied;
  /** Customer filter attribute definitions (from customers/filters API) for Custom Fields tab */
  customerFilterAttributes?: CustomerFilterAttribute[];
}

const initialState: FiltersState = {
  filters: {
    sortField: { field: '', direction: SortDirection.ASCENDING },
  },
  epoch: {
    [EpochScreenType.CAMPAIGN_DETAIL]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.CAMPAIGN_LIST]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.BEAT_PLANS]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.SEGMENT_LIST]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.CREATE_BEAT_PLAN]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.SEND_CAMPAIGN]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.SALES_TEAM_ACTIVITY]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.REPORT]: {
      startDate: new Date(moment().startOf('week').toDate()),
      endDate: new Date(moment().format('YYYY-MM-DD')),
    },
    [EpochScreenType.ORDERLIST]: {
      startDate: new Date(moment().subtract(7, 'days').format()),
      endDate: new Date(moment().format()),
    },
    [EpochScreenType.INVOICELIST]: {
      startDate: new Date(moment().subtract(7, 'days').format()),
      endDate: new Date(moment().format()),
    },
    [EpochScreenType.REVENUE_BREAKDOWN]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format()),
    },
    [EpochScreenType.CAMPAIGN_DAY_WISE]: {
      startDate: new Date(moment().subtract(7, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format()),
    },
    [EpochScreenType.CAMPAIGN_REVENUE]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format()),
    },
    [EpochScreenType.HOME]: {
      startDate: new Date(moment().startOf('quarter').toDate()),
      endDate: new Date(moment().toDate()),
    },
    [EpochScreenType.SALES_DASHBOARD]: {
      startDate: new Date(moment().startOf('quarter').toDate()),
      endDate: new Date(moment().toDate()),
    },
    [EpochScreenType.CONSUMPTION_METRICS]: {
      startDate: new Date(moment().startOf('week').toDate()),
      endDate: new Date(moment().endOf('week').toDate()),
    },
    [EpochScreenType.GROW_CONVERSATION_LIST]: {
      startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
      endDate: new Date(moment().format()),
    },
  },
  sift: {
    [SiftScreenEnum.APPLIED_FILTERS]: {},
    [SiftScreenEnum.PRODUCT_PERFORMANCE_REPORTS]: undefined,
    [SiftScreenEnum.SEARCH_SELECT_PRODCUTS]: undefined,
    [SiftScreenEnum.CUSTOMER_PERFORMANCE_REPORTS]: undefined,
    [SiftScreenEnum.INVOICE_REPORTS]: undefined,
    [SiftScreenEnum.SALES_TEAM_CHECKINS_REPORT]: undefined,
    [SiftScreenEnum.CAMPAIGN_CUSTOMER_REPORTS]: undefined,
    [SiftScreenEnum.PAYMENTS_REPORT]: undefined,
    [SiftScreenEnum.SUPPLY_TRACKER]: undefined,
    [SiftScreenEnum.BEAT_PLANS_SCREEN]: undefined,
    [SiftScreenEnum.CAMPAIGN_ATTEMPTS]: undefined,
    [SiftScreenEnum.PRICE_LIST_SCREEN]: undefined,
    [SiftScreenEnum.ORDER_DETAILS_REPORT]: undefined,
    [SiftScreenEnum.SALES_LEADERBOARD_REPORT]: undefined,
    [SiftScreenEnum.SALES_CLOCKIN_REPORT]: undefined,
    [SiftScreenEnum.CAMPAIGN_LIST_SCREEN]: undefined,
    [SiftScreenEnum.SEND_CAMPAIGN_SCREEN]: undefined,
    [SiftScreenEnum.CUSTOMER_SCREEN]: undefined,
    [SiftScreenEnum.PRODUCTS_SCREEN]: undefined,
    [SiftScreenEnum.CFA_SCREEN]: undefined,
    [SiftScreenEnum.DYNAMIC_SEGMENT_SCREEN]: undefined,
    [SiftScreenEnum.ORDER_LINE_SCREEN]: undefined,
    [SiftScreenEnum.PRINCIPAL_ORDER_SCREEN]: undefined,
    [SiftScreenEnum.PRINCIPAL_INVOICE_SCREEN]: undefined,
    [SiftScreenEnum.PAYMENTS_SCREEN]: undefined,
    [SiftScreenEnum.SEGMENTS_SCREEN]: undefined,
    [SiftScreenEnum.GROUPS_SCREEN]: undefined,
    [SiftScreenEnum.SEGMENTS_DETAILS_SCREEN]: undefined,
    [SiftScreenEnum.TURNOVER_CUSTOMERS]: undefined,
    [SiftScreenEnum.TURNOVER_SCHEMES]: undefined,
    [SiftScreenEnum.TURNOVER_SCHEME_DETAILS]: undefined,
    [SiftScreenEnum.TURNOVER_CUSTOMER_DETAILS]: undefined,
    [SiftScreenEnum.TURNOVER_INVOICE_DETAILS]: undefined,
    [SiftScreenEnum.CUSTOEMER_CONTACT_SELECTION]: undefined,
    [SiftScreenEnum.TURNOVER_PENDING_INVOICES]: undefined,
    [SiftScreenEnum.TURNOVER_SCHEME_REPORT]: undefined,
    [SiftScreenEnum.CUSTOMERS_ERP_SYNC_DETAILS]: {
      taskStatus: [
        {
          id: 'Successful',
          value: 'Successful',
          selected: false,
          eitherOr: true,
        },
        { id: 'Failed', value: 'Failed', selected: false, eitherOr: true },
      ],
    },
    [SiftScreenEnum.DYNAMIC_SEGMENTS_REPORTS]: undefined,
    [SiftScreenEnum.DYNAMIC_SEGMENTS_REPORTS_DETAILS]: undefined,
    [SiftScreenEnum.TYPEBOT_USER_RESPONSES]: undefined,
    [SiftScreenEnum.SCHEMES_SCREEN]: undefined,
    [SiftScreenEnum.TEAM_INVITES_SCREEN]: undefined,
    [SiftScreenEnum.SALES_TEAM_SCREEN]: undefined,
    [SiftScreenEnum.WHATSAPP_TEMPLATES]: undefined,
    [SiftScreenEnum.SALES_DASHBOARD]: undefined,
  },
  threadsFilters: {
    transactionStatus: {
      campaign: [],
      dispatch: [],
      invoice: [],
      order: [],
      payment: [],
      priceList: [],
    },
    others: {
      Type: [],
      Status: [],
      Dispatch: [],
      Whatsapp: [],
    },
    channelType: [],
    routes: [],
    customerSegment: [],
    teamMember: [],
    customer: [],
    cfaList: [],
    divisionList: [],
    issueThreadTransaction: [],
    filteredChannels: null,
  },
  recentSearches: {
    products: [],
    schemes: [],
    orders: [],
    trackPos: [],
    cart: [],
    returns: [],
    ledger: [],
    invoice: [],
    threads: [],
    customers: [],
    tags: [],
    payment: [],
    members: [],
    campaigns: [],
  },
  appliedFilters: {
    sortField: { field: '', direction: SortDirection.ASCENDING },
  },
  error: null,
  searchKey: '',
  imageSearchKey: { path: '', type: '' },
  searchActive: false,
  searchActiveScreen: '',
  pagination: 20,
  mobileFilters: {},
  mobileSearch: {},
  appliedMobileFilters: {},
  customerFilterAttributes: [],
};

export const filtersSlice = createSlice({
  name: FILTER_FEATURE_KEY,
  initialState,
  reducers: {
    addListInputItems(
      state,
      action: PayloadAction<{
        inputItems: InputItem[];
        key: string;
        parentIdRequired?: boolean;
      }>
    ) {
      const key = action.payload.key as ListInputItemOptions;
      if (state.filters) {
        if (state.filters.listInputItem) {
          state.filters.listInputItem[key] = action.payload.inputItems;
        } else {
          // @ts-ignore
          state.filters.listInputItem = { [key]: action.payload.inputItems };
        }
      }
    },
    addListInputItemsWithSearch(
      state,
      action: PayloadAction<{
        inputItems: InputItem[];
        key: string;
        parentIdRequired?: boolean;
      }>
    ) {
      const key = action.payload.key as ListInputItemOptionsWithSearch;
      if (state.filters) {
        if (state.filters.listInputItemWithSearch) {
          state.filters.listInputItemWithSearch[key] =
            action.payload.inputItems;
        } else {
          // @ts-ignore
          state.filters.listInputItemWithSearch = {
            [key]: action.payload.inputItems,
          };
        }
      }
    },
    addTreeInputItems(
      state,
      action: PayloadAction<{
        inputItems: TreeInput[];
        key: string;
        parentIdRequired?: boolean;
      }>
    ) {
      const key = action.payload.key as TreeInputItemOptions;
      if (state.filters) {
        if (state.filters.treeInputItem) {
          state.filters.treeInputItem[key] = action.payload.inputItems;
        } else {
          // @ts-ignore
          state.filters.treeInputItem = { [key]: action.payload.inputItems };
        }
      }
    },
    addSearchListInputItems(
      state,
      action: PayloadAction<{
        value: {
          searchTerm: string;
          listInputItem: InputItem[];
        };
        key: string;
      }>
    ) {
      const key = action.payload.key as ListInputItemOptions;
      if (state.filters) {
        if (state.filters.searchListInputItem) {
          state.filters.searchListInputItem[key] = action.payload.value;
        } else {
          // @ts-ignore
          state.filters.searchListInputItem = {
            [key]: action.payload.value,
          };
        }
      }
    },
    addStringInputItems(
      state,
      action: PayloadAction<{
        value: string;
        key: string;
      }>
    ) {
      const key = action.payload.key as StringInputItemOptions;
      const data = action.payload.value;
      if (state.filters) {
        if (state.filters.stringInputItem) {
          state.filters.stringInputItem[key] = data;
        } else {
          // @ts-ignore
          state.filters.stringInputItem = { [key]: data };
        }
      }
    },
    updateListInputItem(
      state,
      action: PayloadAction<{ value: InputItem[]; key: string }>
    ) {
      if (state.filters.listInputItem) {
        state.filters.listInputItem[
          action.payload.key as ListInputItemOptions
        ] = action.payload.value;
      }
    },
    updateListInputItemWithSearch(
      state,
      action: PayloadAction<{ value: InputItem[]; key: string }>
    ) {
      if (state.filters.listInputItemWithSearch) {
        state.filters.listInputItemWithSearch[
          action.payload.key as ListInputItemOptionsWithSearch
        ] = action.payload.value;
      }
    },
    updateTreeInputItem(
      state,
      action: PayloadAction<{ value: TreeInput[]; key: string }>
    ) {
      if (state.filters.treeInputItem) {
        state.filters.treeInputItem[
          action.payload.key as TreeInputItemOptions
        ] = action.payload.value;
      }
    },
    deleteListInputItem(state, action: PayloadAction<{ key: string }>) {
      const newState: any = _.omit(
        state.filters.listInputItem,
        action.payload.key
      );
      if (state.filters.listInputItem) {
        state.filters.listInputItem[
          action.payload.key as ListInputItemOptions
        ] = newState;
      }
    },
    updateSearchListInputItem(
      state,
      action: PayloadAction<{
        value: {
          searchTerm: string;
          listInputItem: InputItem[];
        };
        key: string;
      }>
    ) {
      if (state.filters.searchListInputItem) {
        state.filters.searchListInputItem[
          action.payload.key as ListInputItemOptions
        ] = action.payload.value;
      }
    },
    updateStringInputItems(
      state,
      action: PayloadAction<{
        value: string | number;
        key: string;
      }>
    ) {
      if (state.filters.stringInputItem) {
        state.filters.stringInputItem[
          action.payload.key as StringInputItemOptions
        ] = action.payload.value.toString();
      }
    },
    updateCalendarInputItem(
      state,
      action: PayloadAction<{
        value: string;
        key: string;
      }>
    ) {
      if (state.filters.calendarInputItem) {
        state.filters.calendarInputItem[action.payload.key] =
          action.payload.value;
      }
      state.filters.calendarInputItem = {
        [action.payload.key]: action.payload.value,
      };
    },
    updateAppliedFilters(state) {
      state.appliedFilters = state.filters;
    },
    updateAppliedFiltersByScreenAndValue(
      state,
      action: PayloadAction<{ screen: SiftScreenEnum; value: any }>
    ) {
      state.sift[SiftScreenEnum.APPLIED_FILTERS][action?.payload?.screen] =
        action?.payload?.value;
    },
    clearAppliedFiltersByScreen(
      state,
      action: PayloadAction<{ screen: SiftScreenEnum }>
    ) {
      state.sift[SiftScreenEnum.APPLIED_FILTERS][action?.payload?.screen] =
        undefined;

      const setSelectedToFalse = (obj: any) => {
        for (const key in obj) {
          if (typeof obj[key] === 'object' && obj[key] !== null) {
            setSelectedToFalse(obj[key]);
          } else if (key === 'selected' && obj[key] === true) {
            obj[key] = false;
          }
        }
        return obj;
      };

      // Call the function on the data object
      state.sift[action?.payload?.screen] = setSelectedToFalse(
        state.sift[action?.payload?.screen]
      );
    },
    updateAppliedFiltersByScreen(
      state,
      action: PayloadAction<{ screen: SiftScreenEnum }>
    ) {
      const screenFilters = state.sift?.[action?.payload?.screen];
      if (screenFilters) {
        const appliedFilters = Object.entries(screenFilters).reduce(
          (acc, [key, value]) => {
            if (Array.isArray(value)) {
              const selectedItems = value
                .filter((item) => item.selected)
                .map((item) => item.id);
              if (selectedItems.length > 0) {
                acc[key] = selectedItems;
              }
            }
            return acc;
          },
          {} as Record<string, string[]>
        );

        state.sift[SiftScreenEnum.APPLIED_FILTERS][action?.payload?.screen] =
          appliedFilters;
      }
    },
    clearAppliedFilter(state, action: PayloadAction<undefined | SortField>) {
      state.appliedFilters = {
        sortField: action.payload || {
          field: '',
          direction: SortDirection.ASCENDING,
        },
      };
      const filters = { ...state.filters };
      filters.sortField = action.payload || {
        field: '',
        direction: SortDirection.ASCENDING,
      };
      const {
        listInputItem,
        stringInputItem,
        booleanInputItem,
        treeInputItem,
        calendarInputItem,
      } = filters;
      if (!isUndefined(listInputItem) && !isEmpty(listInputItem)) {
        const listInput = { ...listInputItem };
        filters.listInputItem = Object.fromEntries(
          Object.entries(listInput)?.map(([k, v = []]) => [
            k,
            [...v]?.map((item) => ({
              ...item,
              // selected: resetAllFilters && item?.id === 'all',
              selected: false,
            })) || [],
          ]) || []
        ) as ListInputItem;
      }
      if (!isUndefined(stringInputItem) && !isEmpty(stringInputItem)) {
        const stringInput = { ...stringInputItem };
        filters.stringInputItem = Object.fromEntries(
          Object.entries(stringInput)?.map(([k, _v]) => [k, ''])
        ) as StringInputItem;
      }
      if (!isUndefined(booleanInputItem) && !isEmpty(booleanInputItem)) {
        const booleanInput = { ...booleanInputItem };
        filters.booleanInputItem = Object.fromEntries(
          Object.entries(booleanInput)?.map(([k, _v]) => [k, false])
        ) as BooleanInputItem;
      }

      if (!isUndefined(treeInputItem) && !isEmpty(treeInputItem)) {
        const clearSelectedTreeFilters = (treeData: any[]) => {
          return treeData.map((node) => {
            if ('selected' in node) {
              node.selected = false;
            }
            if (node?.TreeData && Array.isArray(node?.TreeData)) {
              node.TreeData = clearSelectedTreeFilters(node?.TreeData);
            }
            return node;
          });
        };

        const treeInput = { ...treeInputItem };
        if (treeInput.Routes) {
          treeInput.Routes = clearSelectedTreeFilters(treeInput?.Routes);
        }
        if (treeInput.Segments) {
          treeInput.Segments = clearSelectedTreeFilters(treeInput?.Segments);
        }
        filters.treeInputItem = treeInput;
      }
      if (!isUndefined(calendarInputItem) && !isEmpty(calendarInputItem)) {
        const calendarInput = { ...calendarInputItem };
        filters.calendarInputItem = Object.fromEntries(
          Object.entries(calendarInput)?.map(([k, _v]) => [k, ''])
        ) as CalendarInputItem;
      }
      filters.customAttributeFilter = [];
      state.filters = filters;
    },
    updateCustomAttributeFilter(
      state,
      action: PayloadAction<CustomAttributeFilterItem[]>
    ) {
      state.filters.customAttributeFilter = action.payload;
    },
    setCustomerFilterAttributes(
      state,
      action: PayloadAction<CustomerFilterAttribute[]>
    ) {
      state.customerFilterAttributes = action.payload ?? [];
    },
    addBooleanInputItem(
      state,
      action: PayloadAction<{ key: string; value: boolean }>
    ) {
      const key = action.payload.key as BooleanInputItemOptions;
      const data = action.payload.value;
      if (state.filters) {
        if (state.filters.booleanInputItem) {
          state.filters.booleanInputItem[key] = data;
        } else {
          // @ts-ignore
          state.filters.booleanInputItem = { [key]: data };
        }
      }
    },
    updateBooleanInputItem(
      state,
      action: PayloadAction<{ key: string; value: boolean }>
    ) {
      if (state.filters.booleanInputItem) {
        state.filters.booleanInputItem[
          action.payload.key as BooleanInputItemOptions
        ] = action.payload.value;
      }
    },
    updateSearchKey(state, action: PayloadAction<string>) {
      state.searchKey = action.payload;
    },
    updateImageSearchKey(state, action: PayloadAction<ImageSearchKey>) {
      state.imageSearchKey = action.payload;
    },
    updateSearchKeyList(state, action: PayloadAction<string[]>) {
      state.searchKeyList = action.payload;
    },
    addSearchKeyToList(state, action: PayloadAction<string>) {
      if (state.searchKeyList) {
        if (!state.searchKeyList.includes(action.payload)) {
          state.searchKeyList?.push(action.payload);
        }
      } else {
        state.searchKeyList = [action.payload];
      }
    },
    updateSearchActive(state, action: PayloadAction<boolean>) {
      state.searchActive = action.payload;
    },
    updateSearchActiveScreen(state, action: PayloadAction<string>) {
      state.searchActiveScreen = action.payload;
    },
    updatePaginationKey(state, action: PayloadAction<number>) {
      state.pagination = action.payload;
    },
    updateRecentSearches(
      state,
      action: PayloadAction<{ typed: RecentSearchTypes; searchTerm: string }>
    ) {
      const { typed, searchTerm } = action.payload;
      if (!state.recentSearches[typed]?.includes(searchTerm)) {
        state.recentSearches[typed] = [
          ...state.recentSearches[typed],
          searchTerm,
        ];
      }
    },
    clearRecentSearches(state, action: PayloadAction<RecentSearchTypes>) {
      const typed = action.payload;
      state.recentSearches[typed] = [];
    },
    addOperationInputItem(
      state,
      action: PayloadAction<{ key: string; operator: Operator; value: number }>
    ) {
      if (!state.filters.arithmeticOperatorInputItem) {
        // @ts-ignore
        state.filters.arithmeticOperatorInputItem = {};
      }
      // @ts-ignore
      state.filters.arithmeticOperatorInputItem[action.payload.key] = {
        operator: action.payload.operator,
        value: action.payload.value,
      };
    },
    updateOperationInputItem(
      state,
      action: PayloadAction<{ key: string; operator: Operator; value: number }>
    ) {
      const { key, operator, value } = action.payload;
      if (state.filters.arithmeticOperatorInputItem) {
        state.filters.arithmeticOperatorInputItem[
          key as ArithmeticOperatorInputOptions
        ] = {
          operator,
          value,
        };
      }
    },
    updateEpochTimeWithScreen(
      state,
      action: PayloadAction<{
        screen: EpochScreenType;
        startDate: Date;
        endDate: Date;
      }>
    ) {
      state.epoch[action.payload.screen] = {
        startDate: action?.payload?.startDate,
        endDate: action?.payload?.endDate,
      };
    },
    updateSiftByScreen(
      state,
      action: PayloadAction<{ screen: keyof Sift; filters: any }>
    ) {
      state.sift = {
        ...state.sift,
        [action.payload.screen]: action.payload.filters,
      };
    },
    updateIndividualSiftByKeyAndScreen(
      state,
      action: PayloadAction<{
        value: InputItem[];
        key: string;
        screen: SiftScreenEnum;
      }>
    ) {
      const currentSiftValue = state.sift[action.payload.screen];
      console.log('currentSiftValue', currentSiftValue);
      if (currentSiftValue) {
        currentSiftValue[action.payload.key] = action.payload.value;
      }
      state.sift = {
        ...state.sift,
        [action.payload.screen]: currentSiftValue,
      };
    },
    updateAppliedThreadsFitler(
      state,
      action: PayloadAction<{ threadsFilters: ThreadsFitlers }>
    ) {
      state.threadsFilters = action?.payload?.threadsFilters;
    },
    updateMobileFiltersByScreen(
      state,
      action: PayloadAction<{
        screen: MobileSiftScreen;
        filter: FiltersData;
        loading?: boolean;
      }>
    ) {
      const { screen, filter, loading } = action.payload;
      const existingFilters = {
        ...state.mobileFilters[screen]?.filters,
      } as FiltersData;
      const { updatedFilter, defaultSort, hasSelectedSort, defaultDateRange } =
        updateFiltersData(existingFilters, filter);
      state.mobileFilters[screen] = {
        loading: loading ?? state.mobileFilters[screen]?.loading ?? false,
        filters: updatedFilter,
      };
      if (!hasSelectedSort && defaultSort) {
        state.appliedMobileFilters[screen] = {
          ...state.appliedMobileFilters[screen],
          sortFilter: defaultSort,
        };
      }
      if (!state.appliedMobileFilters[screen]?.dateRange && defaultDateRange) {
        state.appliedMobileFilters[screen] = {
          ...state.appliedMobileFilters[screen],
          dateRange: { ...defaultDateRange, isDefault: true },
        };
      }
    },
    updateMobileSearchByScreen(
      state,
      action: PayloadAction<{
        screen: MobileSiftScreen;
        active?: boolean;
        searchKey?: string;
      }>
    ) {
      const { screen, active, searchKey } = action.payload;
      const searchFilter = state.mobileSearch?.[screen] || {
        searchKey: '',
        active: false,
      };

      if (!isUndefined(active)) {
        searchFilter.active = active;
      }
      if (!isUndefined(searchKey)) {
        searchFilter.searchKey = searchKey;
      }

      state.mobileSearch[screen] = searchFilter;
    },
    updateMobileAppliedFiltersByScreen(
      state,
      action: PayloadAction<{
        screen: MobileSiftScreen;
        customHandler?: AppliedFiltersData;
      }>
    ) {
      const { screen, customHandler } = action.payload;
      const filters = { ...state.mobileFilters[screen]?.filters };
      const appliedFilters = applySelectedFilters(filters);
      state.appliedMobileFilters[screen] = {
        ...appliedFilters,
        ...customHandler,
      };
    },
    clearMobileAppliedFiltersByScreen(
      state,
      action: PayloadAction<{
        screen: MobileSiftScreen;
      }>
    ) {
      const { screen } = action.payload;
      const { filters = {} } = { ...state.mobileFilters[screen] };
      state.mobileFilters[screen] = {
        loading: false,
        filters: clearSelectedFilters(filters),
      };
      state.appliedMobileFilters[screen] = {};
    },
    clearSearch(state) {
      state.searchActive = false;
      state.searchKey = '';
    },
  },
});

export const {
  addListInputItemsWithSearch,
  updateTreeInputItem,
  updateListInputItem,
  addListInputItems,
  addTreeInputItems,
  deleteListInputItem,
  updateCalendarInputItem,
  addSearchListInputItems,
  updateSearchListInputItem,
  updateAppliedFilters,
  addStringInputItems,
  updateStringInputItems,
  addBooleanInputItem,
  updateBooleanInputItem,
  updateSearchKey,
  updateImageSearchKey,
  updateSearchActive,
  clearAppliedFilter,
  addOperationInputItem,
  updateOperationInputItem,
  updatePaginationKey,
  updateSearchKeyList,
  addSearchKeyToList,
  updateSearchActiveScreen,
  updateRecentSearches,
  clearRecentSearches,
  updateEpochTimeWithScreen,
  updateSiftByScreen,
  updateAppliedFiltersByScreen,
  updateIndividualSiftByKeyAndScreen,
  updateAppliedFiltersByScreenAndValue,
  clearAppliedFiltersByScreen,
  updateAppliedThreadsFitler,
  updateMobileFiltersByScreen,
  updateMobileAppliedFiltersByScreen,
  clearMobileAppliedFiltersByScreen,
  updateMobileSearchByScreen,
  updateListInputItemWithSearch,
  clearSearch,
  updateCustomAttributeFilter,
  setCustomerFilterAttributes,
} = filtersSlice.actions;

export const getFiltersSliceState = (rootState: RootState): FiltersState =>
  rootState[FILTER_FEATURE_KEY];

export const getFilters = createSelector(
  getFiltersSliceState,
  (state) => state.filters
);
export const getAppliedFilters = createSelector(
  getFiltersSliceState,
  (state) => state.appliedFilters as FilterInputs
);

export const getCustomerFilterAttributes = createSelector(
  getFiltersSliceState,
  (state) => state.customerFilterAttributes ?? []
);

export const getSearchKey = createSelector(
  getFiltersSliceState,
  (state) => state?.searchKey
);
export const getImageSearchKey = createSelector(
  getFiltersSliceState,
  (state) => state.imageSearchKey
);

export const getSearchActive = createSelector(
  getFiltersSliceState,
  (state) => state?.searchActive
);

export const getSearchActiveScreen = createSelector(
  getFiltersSliceState,
  (state) => state?.searchActiveScreen
);

export const getPaginationKey = createSelector(
  getFiltersSliceState,
  (state) => state?.pagination
);

export const getSearchKeyList = createSelector(
  getFiltersSliceState,
  (state) => state?.searchKeyList
);

export const getRecentSearches = createSelector(
  getFiltersSliceState,
  (state) => state?.recentSearches
);

export const getEpochRangeWithScreen = (screen: EpochScreenType) => {
  return createSelector(
    getFiltersSliceState,
    (state) => state?.epoch?.[screen]
  );
};

export const getSiftByScreen = (screen: SiftScreenEnum) => {
  return createSelector(getFiltersSliceState, (state) => state?.sift?.[screen]);
};

export const getAppliedFiltersByScreen = (
  screen: SiftScreenEnum | undefined
) => {
  return createSelector(getFiltersSliceState, (state) =>
    screen ? state?.sift[SiftScreenEnum.APPLIED_FILTERS][screen] : {}
  );
};

export const getValidAppliedFiltersByScreen = (
  screen: SiftScreenEnum | undefined
) => {
  return createSelector(getFiltersSliceState, (state) => {
    const currentScreenFilters = screen ? state?.sift?.[screen] : {};
    const currentScreenKeys =
      currentScreenFilters && !_.isEmpty(currentScreenFilters)
        ? Object.keys(currentScreenFilters)
        : [];
    const shiftScreenAppliedFilters =
      screen && state?.sift && state?.sift?.[SiftScreenEnum.APPLIED_FILTERS]
        ? state?.sift?.[SiftScreenEnum.APPLIED_FILTERS]?.[screen]
        : {};

    let validAppliedFilters = {};
    !_.isEmpty(shiftScreenAppliedFilters) &&
      Object.keys(shiftScreenAppliedFilters)?.forEach((key) => {
        if (currentScreenKeys?.includes(key)) {
          validAppliedFilters = {
            ...validAppliedFilters,
            [key]: shiftScreenAppliedFilters[key],
          };
        }
      });
    return validAppliedFilters;
  });
};

export const getAppliedThreadsFilter = createSelector(
  getFiltersSliceState,
  (state) => state?.threadsFilters
);

export const getMobileFilterByScreen = (screen: MobileSiftScreen) =>
  createSelector(
    getFiltersSliceState,
    (state) =>
      state.mobileFilters[screen] || {
        loading: false,
        filters: {},
      }
  );

export const getMobileAppliedFilterByScreen = (screen: MobileSiftScreen) =>
  createSelector(
    getFiltersSliceState,
    (state) => state.appliedMobileFilters?.[screen]
  );

export const getMobileSearchByScreen = (screen: MobileSiftScreen) =>
  createSelector(
    getFiltersSliceState,
    (state) => state?.mobileSearch?.[screen]
  );
